import type { User } from "@supabase/supabase-js";

import { supabase } from "@/integrations/supabase/client";

const buildDisplayName = (user: User) =>
  user.user_metadata?.display_name ||
  user.user_metadata?.full_name ||
  user.email?.split("@")[0] ||
  "Kindred member";

const isMissingOnboardingColumn = (error: { message?: string | null; details?: string | null; hint?: string | null } | null) =>
  [error?.message, error?.details, error?.hint].some(
    (value) => typeof value === "string" && value.includes("onboarding_completed"),
  );

export const ensureProfile = async (user: User) => {
  const { error } = await supabase.from("profiles").upsert(
    {
      id: user.id,
      display_name: buildDisplayName(user),
    },
    { onConflict: "id" },
  );

  if (error) {
    console.error("Error ensuring profile exists:", error);
  }
};

export const getOnboardingCompletion = async (userId: string) => {
  const { data, error } = await supabase
    .from("my_private_profile")
    .select("onboarding_completed, postcode")
    .maybeSingle();

  if (isMissingOnboardingColumn(error)) {
    const { data: legacyData, error: legacyError } = await supabase
      .from("my_private_profile")
      .select("postcode")
      .maybeSingle();

    if (legacyError) {
      console.error("Error fetching legacy onboarding state:", legacyError);
    }

    return Boolean(legacyData?.postcode);
  }

  if (error) {
    console.error("Error fetching onboarding state:", error);
    return false;
  }

  return Boolean(data?.onboarding_completed);
};

export const finishOnboardingProfile = async (userId: string, lookingFor: string[]) => {
  const { error } = await supabase
    .from("profiles")
    .update({ looking_for: lookingFor, onboarding_completed: true })
    .eq("id", userId);

  if (isMissingOnboardingColumn(error)) {
    return supabase.from("profiles").update({ looking_for: lookingFor }).eq("id", userId);
  }

  return { error };
};
